import React, { useEffect, useRef } from 'react';

import { useGSAP } from '@/hooks/useGSAP';
import { useCookieConsent } from '@/contexts/CookieConsentContext';

import styles from './CookieConsent.module.css';

const getBrowserLanguage = () => {
  if (typeof navigator === 'undefined') return 'en';
  const lang = navigator.language || navigator.languages?.[0] || 'en';
  return lang.startsWith('fr') ? 'fr' : 'en';
};

const LANG = getBrowserLanguage();

const TRANSLATIONS = {
  en: {
    text: 'We use cookies to analyze our traffic and improve your experience. By clicking "Accept", you consent to our use of cookies.',
    accept: 'Accept',
    decline: 'Decline',
  },
  fr: {
    text: 'Nous utilisons des cookies pour analyser notre trafic et améliorer votre expérience. En cliquant sur « Accepter », vous consentez à l\u2019utilisation de cookies.',
    accept: 'Accepter',
    decline: 'Refuser',
  },
} as const;

const t = TRANSLATIONS[LANG];

export const CookieConsent: React.FC = () => {
  const { hasDecided, acceptAll, declineAll } = useCookieConsent();
  const bannerRef = useRef<HTMLDivElement>(null);
  const { gsap, ready } = useGSAP();
  const animatedIn = useRef(false);
  const dismissing = useRef(false);

  // Slide-up entrance with 1.5s delay
  useEffect(() => {
    if (!ready || !gsap || hasDecided || animatedIn.current) return;
    const el = bannerRef.current;
    if (!el) return;

    gsap.set(el, { y: 100, autoAlpha: 0 });
    gsap.to(el, {
      y: 0,
      autoAlpha: 1,
      duration: 0.6,
      delay: 1.5,
      ease: 'power3.out',
      onComplete: () => {
        el.focus();
      },
    });
    animatedIn.current = true;
  }, [ready, gsap, hasDecided]);

  // Slide-down exit animation
  const animateOut = (callback: () => void) => {
    if (dismissing.current) return;
    dismissing.current = true;

    const el = bannerRef.current;
    if (!gsap || !el) {
      callback();
      return;
    }
    gsap.to(el, {
      y: 100,
      autoAlpha: 0,
      duration: 0.4,
      ease: 'power3.in',
      onComplete: callback,
    });
  };

  const handleAccept = () => animateOut(acceptAll);
  const handleDecline = () => animateOut(declineAll);

  if (hasDecided) return null;

  return (
    <div
      ref={bannerRef}
      className={styles.banner}
      role="dialog"
      aria-label={LANG === 'fr' ? 'Consentement cookies' : 'Cookie consent'}
      tabIndex={-1}
      style={{ visibility: 'hidden' }}
    >
      <div className={styles.content}>
        <p className={styles.text}>{t.text}</p>
        <div className={styles.actions}>
          <button className={styles.acceptButton} onClick={handleAccept}>
            {t.accept}
          </button>
          <button className={styles.declineButton} onClick={handleDecline}>
            {t.decline}
          </button>
        </div>
      </div>
    </div>
  );
};
